import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../LangContext";

const COPY = {
  sv: {
    title: "Vi värnar din integritet.",
    body:
      "Den här webbplatsen använder endast nödvändiga cookies för att fungera. Vi använder inga spårnings- eller analyscookies. Genom att klicka ”Acceptera” bekräftar du att du tagit del av informationen.",
    accept: "Acceptera",
    decline: "Endast nödvändiga",
    more: "Läs mer",
  },
  en: {
    title: "We respect your privacy.",
    body:
      "This site uses only strictly necessary cookies required for the site to function. We do not use any tracking or analytics cookies. By clicking ‘Accept’ you confirm you have read this notice.",
    accept: "Accept",
    decline: "Necessary only",
    more: "Read more",
  },
};

export default function CookieBanner() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  useEffect(() => {
    try {
      const accepted = localStorage.getItem("np_cookie_consent");
      if (!accepted) setOpen(true);
    } catch (_) {
      setOpen(true);
    }
  }, []);

  const handle = (value) => {
    try {
      localStorage.setItem("np_cookie_consent", value);
      localStorage.setItem("np_cookie_consent_at", new Date().toISOString());
    } catch (_) {}
    setOpen(false);
  };

  const c = COPY[lang] || COPY.sv;
  const policy =
    lang === "en"
      ? {
          title: "Privacy & cookies",
          body: [
            "Personal data submitted via the contact form (name, email, phone, message) is processed solely to respond to your enquiry. The legal basis is GDPR Art. 6(1)(f) — legitimate interest. Data is stored securely and is not shared with third parties beyond the email service provider used to deliver our reply.",
            "Cookies: only strictly necessary cookies are used (language preference and consent record stored in your browser via localStorage). No analytics or marketing cookies are set.",
            "You may at any time request access, correction or deletion of your data by emailing kontakt@bmjuridik.se.",
          ],
        }
      : {
          title: "Integritet & cookies",
          body: [
            "Personuppgifter som du skickar via kontaktformuläret (namn, e-post, telefon, meddelande) behandlas enbart för att besvara din förfrågan. Rättslig grund är GDPR art. 6.1(f) — berättigat intresse. Uppgifterna lagras säkert och delas inte med tredje part utöver den e-posttjänst vi använder för att leverera vårt svar.",
            "Cookies: enbart strikt nödvändiga cookies används (språkval och samtyckes­markering sparas i webbläsaren via localStorage). Inga analys- eller marknadscookies sätts.",
            "Du kan när som helst begära registerutdrag, rättelse eller radering av dina uppgifter via kontakt@bmjuridik.se.",
          ],
        };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cookie-banner"
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[60] bg-cream border border-line shadow-[0_8px_30px_rgba(0,0,0,0.08)] rounded-sm p-6"
          >
            <p className="font-serif text-xl text-ink leading-snug">{c.title}</p>
            <p className="mt-3 text-sm text-ink2 leading-relaxed">{c.body}</p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                onClick={() => handle("accepted")}
                data-testid="cookie-accept"
                className="inline-flex items-center px-5 py-2.5 rounded-full bg-ink text-cream text-xs font-medium tracking-wide hover:bg-ink2 transition-all duration-300"
              >
                {c.accept}
              </button>
              <button
                onClick={() => handle("necessary")}
                data-testid="cookie-decline"
                className="inline-flex items-center px-5 py-2.5 rounded-full border border-line text-ink2 text-xs font-medium tracking-wide hover:text-ink hover:border-ink transition-all duration-300"
              >
                {c.decline}
              </button>
              <button
                onClick={() => setShowPolicy(true)}
                data-testid="cookie-more"
                className="text-xs text-ink2 underline-offset-4 hover:text-ink hover:underline"
              >
                {c.more} →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPolicy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="cookie-policy-modal"
            className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowPolicy(false)}
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-cream max-w-lg w-full rounded-sm p-8 md:p-10 max-h-[80vh] overflow-y-auto"
            >
              <p className="eyebrow text-ink2">— GDPR</p>
              <h3 className="font-serif text-3xl text-ink mt-3 mb-6">{policy.title}</h3>
              <div className="space-y-4 text-base text-ink2 leading-relaxed">
                {policy.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <button
                onClick={() => setShowPolicy(false)}
                data-testid="cookie-policy-close"
                className="mt-8 inline-flex items-center px-5 py-2.5 rounded-full bg-ink text-cream text-xs font-medium tracking-wide"
              >
                {lang === "en" ? "Close" : "Stäng"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
